/**
 * TP-Link Pharos CPE + RADWIN PtP radio-antenna catalog.
 *
 * TP-Link Pharos covers the budget end of outdoor PtP (CPE210/510/710
 * — 13 dBi to 23 dBi at 5 GHz, $50-$150 per unit). RADWIN sits at
 * the carrier-WISP / public-safety end (RADWIN 2000 — up to 500 Mbps
 * over 120 km in 4.9-6 GHz with full TDMA MAC). Two extremes of the
 * 5 GHz PtP market in one file.
 *
 * Verified against tp-link.com + radwin.com product pages and the
 * official datasheet PDFs.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const TPLINK_RADWIN_RADIO_ANTENNAS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // TP-Link Pharos CPE — 5 GHz budget outdoor PtP
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.tp-link.com/us/business-networking/pharos-cpe/cpe510/
  // SRC: https://www.broadbandbuyer.com/products/19209-tp-link-cpe510kit/
  // SRC: https://nellyssecurity.com/products/tp-link-pharos-outdoor-long-range-wireless-5ghz-dual-polarized-directional-mimo-antenna-cpe510
  //      CPE510 (Wi-Fi 4): 5 GHz 802.11n, 13 dBi 2×2 dual-polarised
  //      directional MIMO antenna with dedicated metal reflector,
  //      300 Mbps, 15+ km long-range PtP, TX power adjustable 0-27
  //      dBm / 500 mW, Pharos MAXtream TDMA for PtMP efficiency.
  {
    id: 'tplink-cpe510',
    manufacturer: 'TP-Link',
    model: 'Pharos CPE510',
    category: 'radio-antenna',
    tagline:
      'Pharos CPE510 — 5 GHz Wi-Fi 4 outdoor, 13 dBi dual-pol, 300 Mbps, 15+ km, budget PtP',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/pharos-cpe/cpe510/',
    specs: {
      band: '5 GHz',
      rangeM: 15000,
      gainDbi: 13,
      beamWidthDeg: 45, // 13 dBi panel typical at 5 GHz
      throughputMbps: 300,
      standard: '802.11n',
      mimo: '2×2',
      poeInVoltage: '24 V passive',
      ipRating: 'IPX5',
      operatingTempC: '-30..+70',
      powerConsumptionW: 8,
    },
  },

  // SRC: https://www.tp-link.com/us/business-networking/pharos-cpe/cpe710/
  // SRC: https://www.broadbandbuyer.com/products/41171-tp-link-cpe710/specifications/
  // SRC: https://www.amazon.com/TP-Link-Transmission-Wireless-Injector-CPE710/dp/B08D71HC9L
  //      CPE710 (Wi-Fi 5): 5 GHz 802.11ac, 23 dBi high-gain Cassegrain
  //      antenna with metal reflector, beam-width 9° azimuth / 7°
  //      elevation, 256-QAM modulation @ 80 MHz bandwidth → 867 Mbps,
  //      1× 10/100/1000 GbE shielded port, 15 kV ESD + 6 kV lightning
  //      protection, IP65 waterproof. Pharos Control centralised
  //      management. Free PoE injector included.
  {
    id: 'tplink-cpe710',
    manufacturer: 'TP-Link',
    model: 'Pharos CPE710',
    category: 'radio-antenna',
    tagline:
      'Pharos CPE710 — 5 GHz AC 867 Mbps, 23 dBi Cassegrain, 9°×7° beam, IP65, GbE',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/pharos-cpe/cpe710/',
    specs: {
      band: '5 GHz',
      rangeM: 30000, // 23 dBi at 5 GHz typically delivers 25-35 km LoS
      gainDbi: 23,
      beamWidthDeg: 9, // datasheet-stated 9° azimuth, 7° elevation
      throughputMbps: 867,
      standard: '802.11ac',
      mimo: '2×2',
      poeInVoltage: '24 V passive (free injector included)',
      ipRating: 'IP65',
      operatingTempC: '-30..+70',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // RADWIN 2000 Alpha — carrier-class PtP
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://radwin.com/radwin-2000-point-to-point-series/
  // SRC: https://radwin.com/alpha-point-to-point-radio/
  // SRC: https://www.winncom.com/pdf/Radwin_2000_Alpha_Int/RADWIN_2000_Alpha.pdf
  //      RADWIN 2000 Alpha: PtP carrier radio, 4.9-6.0 GHz coverage
  //      (incl. licence-exempt + licensed 4.9 GHz public-safety bands),
  //      three throughput tiers 50 / 250 / 500 Mbps, MIMO-OFDM with
  //      BPSK/QPSK/16QAM/64QAM/256QAM adaptive modulation + coding,
  //      10/20/40/80 MHz channels (dynamic BW selection), up to 120
  //      km (75 mi) range LoS, IP67 weatherproof, available in
  //      connectorized (BYO antenna) or integrated 21 dBi versions.
  //      Used by ISPs / utilities / public safety / oil-and-gas.
  {
    id: 'radwin-2000-alpha-integrated-21dbi',
    manufacturer: 'RADWIN',
    model: '2000 Alpha (Integrated 21 dBi)',
    category: 'radio-antenna',
    tagline:
      'RADWIN 2000 Alpha — 4.9-6 GHz PtP carrier radio, 500 Mbps, 120 km, MIMO-OFDM',
    verified: true,
    datasheetUrl: 'https://radwin.com/radwin-2000-point-to-point-series/',
    specs: {
      band: '5 GHz', // 4.9-6 GHz; closest schema label
      rangeM: 120000, // up to 120 km LoS
      gainDbi: 21,
      beamWidthDeg: 12, // typical for 21 dBi panel at 5 GHz
      throughputMbps: 500, // top tier; lower-tier 50 / 250 Mbps SKUs exist
      standard: 'proprietary', // RADWIN MIMO-OFDM TDMA, not 802.11
      mimo: '2×2',
      ipRating: 'IP67',
      operatingTempC: '-35..+60',
    },
  },
];
